import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Users, Plus, Trash2, X, Loader2, HardHat } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const blankMember = () => ({ role: "", count: 1, base_wage: "", burden_pct: 25 });

function computeRates(members) {
  let headcount = 0, cost = 0;
  members.forEach((m) => {
    const c = Number(m.count) || 0, w = Number(m.base_wage) || 0, b = Number(m.burden_pct) || 0;
    headcount += c;
    cost += c * w * (1 + b / 100);
  });
  const blended = headcount ? cost / headcount : 0;
  return { headcount, cost, blended };
}

export default function CrewsManager({ open, onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  const [members, setMembers] = useState([blankMember()]);
  const [busy, setBusy] = useState(false);
  const { data: crews = [], isLoading } = useQuery({ queryKey: ["crews"], queryFn: async () => (await api.get(`/crews`)).data, enabled: open });

  const { headcount, cost, blended } = computeRates(members);

  const setMember = (i, f, v) => setMembers((ms) => ms.map((m, j) => (j === i ? { ...m, [f]: v } : m)));
  const addMember = () => setMembers((ms) => [...ms, blankMember()]);
  const delMember = (i) => setMembers((ms) => (ms.length > 1 ? ms.filter((_, j) => j !== i) : ms));

  const add = async () => {
    if (!name.trim()) { toast.error("Enter a crew name"); return; }
    const valid = members.filter((m) => Number(m.base_wage) > 0 && Number(m.count) > 0);
    if (valid.length === 0) { toast.error("Add at least one role with a wage and headcount"); return; }
    setBusy(true);
    try {
      await api.post(`/crews`, {
        name: name.trim(), region: region.trim(),
        members: valid.map((m) => ({ role: m.role.trim() || "Worker", count: Number(m.count), base_wage: Number(m.base_wage), burden_pct: Number(m.burden_pct) || 0 })),
      });
      setName(""); setRegion(""); setMembers([blankMember()]);
      qc.invalidateQueries({ queryKey: ["crews"] });
      toast.success("Crew saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/crews/${id}`); qc.invalidateQueries({ queryKey: ["crews"] }); } catch (e) { toast.error(apiErr(e)); } };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="crews-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-5 shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Labor Crews &amp; Burden</h3>
          <button data-testid="crews-close-btn" onClick={onClose} className="h-8 w-8 grid place-items-center rounded hover:bg-secondary text-muted-foreground"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Build a crew from a mix of roles with local wages plus a labor burden % (payroll taxes, workers&apos; comp, insurance, benefits). The burdened blended hourly rate feeds your labor lines.</p>

        <div className="rounded-lg border border-border p-3 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="crew-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Crew name (e.g. Framing crew)" className="rounded-lg border border-input bg-popover px-3 py-2 text-sm outline-none focus:border-primary" />
            <input data-testid="crew-region-input" value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Region (optional)" className="rounded-lg border border-input bg-popover px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
          <div className="grid grid-cols-[1fr_52px_72px_64px_28px] gap-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-0.5">
            <span>Role / trade</span><span className="text-center">Qty</span><span className="text-center">Wage/hr</span><span className="text-center">Burden%</span><span />
          </div>
          {members.map((m, i) => (
            <div key={i} className="grid grid-cols-[1fr_52px_72px_64px_28px] gap-1.5 items-center" data-testid={`crew-member-${i}`}>
              <input data-testid={`member-role-${i}`} value={m.role} onChange={(e) => setMember(i, "role", e.target.value)} placeholder="Carpenter" className="rounded border border-input bg-popover px-2 py-1.5 text-sm outline-none focus:border-primary" />
              <input data-testid={`member-count-${i}`} type="number" value={m.count} onChange={(e) => setMember(i, "count", e.target.value)} className="rounded border border-input bg-popover px-1 py-1.5 text-sm font-mono text-center outline-none focus:border-primary" />
              <input data-testid={`member-wage-${i}`} type="number" value={m.base_wage} onChange={(e) => setMember(i, "base_wage", e.target.value)} placeholder="38" className="rounded border border-input bg-popover px-1 py-1.5 text-sm font-mono text-center outline-none focus:border-primary" />
              <input data-testid={`member-burden-${i}`} type="number" value={m.burden_pct} onChange={(e) => setMember(i, "burden_pct", e.target.value)} className="rounded border border-input bg-popover px-1 py-1.5 text-sm font-mono text-center outline-none focus:border-primary" />
              <button data-testid={`member-del-${i}`} onClick={() => delMember(i)} className="h-7 w-7 grid place-items-center rounded text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <button data-testid="add-member-btn" onClick={addMember} className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline"><Plus className="h-3.5 w-3.5" /> Add role</button>

          <div className="flex items-center justify-between rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Burdened blended rate · crew cost</span>
            <span className="font-mono font-bold text-emerald-500" data-testid="crew-blended-rate">{money(blended)}/hr · {money(cost)}/hr ({headcount || 0} ppl)</span>
          </div>
          <button data-testid="crew-add-btn" onClick={add} disabled={busy} className="w-full rounded-lg bg-primary text-primary-foreground py-2 font-semibold hover:bg-orange-600 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save crew
          </button>
        </div>

        {isLoading ? (
          <div className="grid place-items-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : crews.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-6">No crews yet. Build your first above.</div>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="crews-list">
            {crews.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2" data-testid={`crew-${c.id}`}>
                <div className="min-w-0">
                  <span className="text-sm font-medium flex items-center gap-2"><HardHat className="h-3.5 w-3.5 text-muted-foreground" />{c.name}{c.region ? <span className="text-[10px] text-muted-foreground">· {c.region}</span> : null}</span>
                  {(c.members || []).length > 0 && <div className="text-[11px] text-muted-foreground truncate">{c.members.map((m) => `${m.count}× ${m.role}`).join(", ")}</div>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-sm tabular-nums">{money(c.blended_rate ?? c.hourly_rate)}/hr</span>
                  <button data-testid={`crew-delete-${c.id}`} onClick={() => del(c.id)} className="h-7 w-7 grid place-items-center rounded text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
